import { useState } from "react";
import type { JobMaterialRequirementDto, MaterialDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n } from "../../i18n/index.js";
import { Plus } from "lucide-react";

export function MaterialPanel({
  jobId,
  materials,
  catalog,
  canEdit,
  onChanged,
}: {
  jobId: string;
  materials: JobMaterialRequirementDto[];
  catalog: MaterialDto[];
  canEdit: boolean;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ materialId: "", plannedQuantity: "1" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.addJobMaterial(jobId, form);
      push(t("jobs.materials.addedToast"));
      setForm({ materialId: "", plannedQuantity: "1" });
      setAdding(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">{t("jobs.materials.title")}</h2>
        {canEdit ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> {t("common.add")}
          </Button>
        ) : null}
      </div>
      {materials.length === 0 ? (
        <p className="text-sm text-ink-muted">{t("jobs.materials.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {materials.map((m) => (
            <li key={m.id} className="rounded border border-line px-3 py-2 text-sm">
              <span className="font-medium text-ink">
                {m.materialSku} · {m.materialName}
              </span>
              <span className="ms-2 text-ink-muted">
                {m.plannedQuantity} {m.unitOfMeasure}
              </span>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <div className="mt-3 space-y-2 border-t border-line pt-3">
          <SelectInput
            label={t("common.fields.material")}
            options={catalog.map((x) => ({ value: x.id, label: `${x.sku} · ${x.name}` }))}
            value={form.materialId}
            onChange={(e) => setForm((f) => ({ ...f, materialId: e.target.value }))}
          />
          <TextInput
            label={t("common.fields.plannedQuantity")}
            value={form.plannedQuantity}
            onChange={(e) => setForm((f) => ({ ...f, plannedQuantity: e.target.value }))}
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={submit}
              disabled={busy || !form.materialId}
            >
              {t("common.save")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
